import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { devicesActions } from '../_actions';
import { deviceService } from '../_services/device.service';
import { popUpActions } from '../_stores/PopUpStore/popUpActions';
import { DEVICE_UPDATE_STATUS, GLOBAL_POPUPS } from '../_constants';
import { useError } from '../_hooks/useError';
import { SUPPORT_ORDERS, supportsSupportOrder } from
    '../_helpers/minihubSupportCompat';
import { grafanaDeviceLogsUrl } from '../_helpers/grafanaLinks';
import './MiniHubSupportPanel.scss';

const LOG_LEVELS = ['none', 'error', 'warn', 'info', 'debug', 'verbose'];

/* What the firmware reports while it updates (UpdateStatusCMD), as django
 * stores it on the device. Same words as the OTA module, resolved here through
 * t() rather than DeviceStatusTranslations, which freezes them at load. */
/* Not a firmware status: the panel's own word for the stretch the firmware
 * says nothing about. After the EFR32 (or after the download, on a WiFi-only
 * update) the ESP32 downloads its image to the partition and reboots -- up to
 * 25 min by SIM -- and django only hears the final OK. */
const INSTALLING_WIFI = 'installing_wifi';
const UPDATE_STATUS_LABELS: { [status: string]: string } = {
    [INSTALLING_WIFI]: 'minihub_support_update_installing_wifi',
    [DEVICE_UPDATE_STATUS.INSTALLING]: 'device_firmware_update_status_installing',
    [DEVICE_UPDATE_STATUS.ERROR]: 'global_error',
    [DEVICE_UPDATE_STATUS.DOWNLOADING]: 'device_firmware_update_status_downloading',
    [DEVICE_UPDATE_STATUS.CANCELLED]: 'common.cancelled',
    [DEVICE_UPDATE_STATUS.OK]: 'global_ok',
    [DEVICE_UPDATE_STATUS.RESETTING]: 'device_firmware_update_status_reseting',
    [DEVICE_UPDATE_STATUS.INSTALLING_BLE]: 'device_firmware_update_status_installing_ble',
    [DEVICE_UPDATE_STATUS.ERROR_UNPACKING_TAR]: 'device_firmware_update_status_error_unpacking_tar',
    [DEVICE_UPDATE_STATUS.ERROR_BLE_RESTORED_TO_PREVIOUS_SUCCESS]:
        'device_firmware_update_status_error_ble_restored_to_previous_success',
    [DEVICE_UPDATE_STATUS.ERROR_BLE_RESTORED_TO_PREVIOUS_FAILED]:
        'device_firmware_update_status_error_ble_restored_to_previous_failed',
    [DEVICE_UPDATE_STATUS.ERROR_BLE_RESTORED_TO_PREVIOUS_NOT_FOUND]:
        'device_firmware_update_status_error_ble_restored_to_previous_not_found',
    [DEVICE_UPDATE_STATUS.UNPACKING]: 'device_firmware_update_status_error_unpacking',
    [DEVICE_UPDATE_STATUS.ERROR_FLASH_SPACE]: 'device_firmware_update_status_error_flash_space',
    [DEVICE_UPDATE_STATUS.ERROR_FWRITE]: 'device_firmware_update_status_error_fwrite',
};
const UPDATE_IN_PROGRESS = [
    DEVICE_UPDATE_STATUS.DOWNLOADING,
    DEVICE_UPDATE_STATUS.INSTALLING,
    DEVICE_UPDATE_STATUS.INSTALLING_BLE,
    DEVICE_UPDATE_STATUS.UNPACKING,
    DEVICE_UPDATE_STATUS.RESETTING,
];
const UPDATE_SETTLED = [
    DEVICE_UPDATE_STATUS.EMPTY,
    DEVICE_UPDATE_STATUS.OK,
    DEVICE_UPDATE_STATUS.FIRST_TIME,
];

/* Values that arrive as firmware identifiers and support reads as sentences.
 * Anything not listed is shown as it came: a new cause or a reset code the
 * table does not know must stay visible, not turn into a blank. */
const WATCHDOG = 'minihub_support_reset_watchdog';
const STATUS_VALUE_LABELS: { [key: string]: { [value: string]: string } } = {
    // django names the ESP32 ROM's reset codes (ESP32_RESET_REASONS); here
    // the twelve names collapse into what support can act on.
    reset_reason_0_name: {
        no_mean: 'minihub_support_reset_none',
        poweron_reset: 'minihub_support_reset_poweron',
        sw_reset: 'minihub_support_reset_software',
        sw_cpu_reset: 'minihub_support_reset_software',
        deepsleep_reset: 'minihub_support_reset_deepsleep',
        ext_cpu_reset: 'minihub_support_reset_external',
        rtcwdt_brown_out_reset: 'minihub_support_reset_brownout',
        owdt_reset: WATCHDOG,
        tg0wdt_sys_reset: WATCHDOG,
        tg1wdt_sys_reset: WATCHDOG,
        rtcwdt_sys_reset: WATCHDOG,
        tgwdt_cpu_reset: WATCHDOG,
        rtcwdt_cpu_reset: WATCHDOG,
        rtcwdt_rtc_reset: WATCHDOG,
    },
    // esp_reset_reason(), from the firmware (BAC-611): the ROM codes read
    // "software reset" for a panic, a stack overflow and an update alike.
    reset_cause: {
        poweron: 'minihub_support_reset_poweron',
        software: 'minihub_support_reset_software',
        panic: 'minihub_support_cause_panic',
        int_wdt: 'minihub_support_cause_int_wdt',
        task_wdt: 'minihub_support_cause_task_wdt',
        wdt: 'minihub_support_reset_watchdog',
        deepsleep: 'minihub_support_reset_deepsleep',
        brownout: 'minihub_support_reset_brownout',
        pwr_glitch: 'minihub_support_reset_brownout',
        external: 'minihub_support_reset_external',
        cpu_lockup: 'minihub_support_cause_cpu_lockup',
        usb: 'minihub_support_reset_external',
        jtag: 'minihub_support_reset_external',
        efuse: 'minihub_support_reset_none',
        unknown: 'minihub_support_reset_none',
    },
    // Why the last update refused to start (BAC-630).
    // status_report_json.c: "open" / "closed", as the EFR32 last reported.
    pairing: {
        open: 'minihub_support_pairing_open_value',
        closed: 'minihub_support_pairing_closed_value',
    },
    // keypad_pairing_reason_name: why the window last opened or closed.
    pairing_reason: {
        button: 'minihub_support_pairing_reason_button',
        timeout: 'minihub_support_pairing_reason_timeout',
        success: 'minihub_support_pairing_reason_success',
        bluetooth: 'minihub_support_pairing_reason_bluetooth',
        wifi: 'minihub_support_pairing_reason_wifi',
        never_paired: 'minihub_support_pairing_reason_never_paired',
        three_reboots: 'minihub_support_pairing_reason_three_reboots',
        unknown: 'minihub_support_pairing_reason_unknown',
    },
    ota_last_fail: {
        none: 'minihub_support_ota_fail_none',
        empty_push: 'minihub_support_ota_fail_empty_push',
        packet_malloc: 'minihub_support_ota_fail_packet_malloc',
        update_status_task: 'minihub_support_ota_fail_update_status_task',
        update_task: 'minihub_support_ota_fail_update_task',
    },
};
STATUS_VALUE_LABELS.reset_reason_1_name = STATUS_VALUE_LABELS.reset_reason_0_name;
// Automatic log upload (firmware log_policy_store.h). "default" is the boot
// policy, which never uploads over the SIM: on a hub on the SIM it means no
// log leaves on its own until support applies one.
STATUS_VALUE_LABELS.log_policy = {
    default: 'minihub_support_log_policy_default',
    saved: 'minihub_support_log_policy_saved',
    ram: 'minihub_support_log_policy_ram',
};
STATUS_VALUE_LABELS.log_sent_link = {
    sim: 'minihub_support_log_sent_sim',
    wlan: 'minihub_support_log_sent_wlan',
    none: 'minihub_support_log_sent_none',
};
// Signal of the link carrying the traffic. 127 is the firmware's "no reading"
// (SERVER_SIGNAL_UNKNOWN); on the SIM the figure is the last AT+CSQ, not one
// taken when asked.
STATUS_VALUE_LABELS.signal_dbm = {
    127: 'minihub_support_signal_unknown',
};
// modem_reset_wiring_t (BAC-707): whether the modem's RESET line is wired.
STATUS_VALUE_LABELS.modem_reset_hw = {
    0: 'minihub_support_modem_reset_unknown',
    1: 'minihub_support_modem_reset_wired',
    2: 'minihub_support_modem_reset_absent',
};
const LINK_MODES = ['auto', 'wifi', 'sim'];

// Whole number within [min, max]; anything unreadable counts as min, which
// for both log fields means "off" rather than a value nobody typed.
function clampInt(text, min, max) {
    const n = parseInt(text, 10);
    if (Number.isNaN(n)) {
        return min;
    }
    return Math.min(max, Math.max(min, n));
}

// The firmware's own numbering for which half an order is about.
const DEVICE_WIFI = 1;
const DEVICE_BLE = 2;
const DEVICE_BOTH = 3;

/**
 * Support orders for a MiniHUB (BAC-610 / BAC-611).
 *
 * Superuser only, and hidden for everyone else -- but the guard that counts is
 * the one in django: this only decides what is drawn.
 *
 * Everything here used to need the box on a bench. That is the wrong place:
 * the device you need to diagnose is the one in the field, and by SIM it does
 * not even upload its logs. What these are not is free -- turning the logs up
 * costs the device data, and a reboot drops whatever it was doing.
 */
export function MiniHubSupportPanel({
    uid = '',
    version = '',
    versionBle = '',
    hasNfc = false,
    hasKeypad = false,
    hasSim = false,
    className = '',
    // Cada cuánto se pregunta por la respuesta y cuánto se espera antes de
    // darla por perdida. Son props para que los tests no tengan que esperar
    // medio minuto de verdad.
    statusPollMs = 2000,
    statusWaitMs = 30000,
    // An update is followed for far longer than a status answer: by SIM the
    // ESP32 alone can take 25 min, and a weak signal stretches that further.
    updateWaitMs = 40 * 60 * 1000,
}) {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { errorMessage, setError } = useError();
    const contentId = `minihub-support-content-${uid}`;

    // Read straight from the store rather than through getEverouUser: that
    // selector walks state.common.authentication and throws on a store that
    // does not have it, and this panel is an addition to the device sheet --
    // it must not be able to take the sheet down with it.
    const isSuperuser = useSelector(
        (state: any) => state?.common?.authentication?.user?.isSuperuser
    );

    const [level, setLevel] = useState('info');
    const [persist, setPersist] = useState(true);
    /* Automatic upload. Sending any of these replaces the device's default
     * policy, which never uploads over the SIM -- so without them "Apply"
     * changes the level of logs nobody receives. Both go together: a policy
     * from the server starts from zero, and at_pct alone would leave no floor
     * between sends. The firmware keeps this policy in RAM: a reboot (an OTA
     * included) brings the default back. */
    const [atPct, setAtPct] = useState('50');
    const [minGapMin, setMinGapMin] = useState('2');
    const [linkMode, setLinkMode] = useState('auto');
    const [versions, setVersions] = useState([] as any[]);
    const [targetVersion, setTargetVersion] = useState('');
    const [targetHalf, setTargetHalf] = useState(DEVICE_BOTH);
    const [status, setStatus] = useState(null as any);
    const [statusError, setStatusError] = useState('');
    const [successfulAction, setSuccessfulAction] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    /* Plegado de entrada: esto lanza órdenes remotas delicadas, no es
     * configuración del día a día. */
    const [isOpen, setIsOpen] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    /* What the device has reported since the update order went out:
     * {phases: [{status, percent}], done, failed, timedOut}. The phases are
     * kept, not replaced: support wants to see that a download reached 100%
     * before the BLE install failed, not just the failure. */
    const [updateProgress, setUpdateProgress] = useState(null as any);
    const [isFollowingUpdate, setIsFollowingUpdate] = useState(false);
    // When the follow-up started, for the clock under the progress.
    const [updateStartedAt, setUpdateStartedAt] = useState(0);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    const installedVersions = { version, versionBle };
    const canCheckStatus = supportsSupportOrder(
        SUPPORT_ORDERS.STATUS, installedVersions);
    const canUseLogs = supportsSupportOrder(
        SUPPORT_ORDERS.LOG_CONFIG, installedVersions)
        && supportsSupportOrder(SUPPORT_ORDERS.LOG_FETCH, installedVersions);
    // Only a box with a modem has two links to pick between, and only the
    // keypad has one (DeviceFunctionality.supportsSim). Offering the choice on
    // the rest is offering "sim" on hardware that has no SIM: the order is
    // accepted, the device drops it, and whoever sent it reads the silence as
    // the device being down.
    const canChangeLink = hasSim && supportsSupportOrder(
        SUPPORT_ORDERS.LINK, installedVersions);
    const hasAccessLists = hasNfc || hasKeypad;
    const canClosePairing = supportsSupportOrder(
        SUPPORT_ORDERS.PAIRING_CLOSE, installedVersions);

    // No guard on the uid here: the panel is mounted with key={device.uid}
    // (DeviceAdvancedConfig), so opening another device unmounts this instance
    // and builds a new one -- an answer in flight resolves into a component
    // that is gone, and React drops the update. A ref comparing uids would be
    // dead code, and its own test would pass because of the key, not because
    // of it.

    const loadVersions = useCallback(async () => {
        try {
            const list = await deviceService.getDeviceFirmwareVersions({ uid });
            setVersions(Array.isArray(list) ? list : []);
        } catch {
            // A panel that cannot list versions is still useful for everything
            // else, so this is not an error banner.
            setVersions([]);
        }
    }, [uid]);

    useEffect(() => {
        if (isSuperuser) loadVersions();
    }, [isSuperuser, loadVersions]);

    /* The logs in Grafana are stamped with the hub's own uid, not with the
     * Ble's this panel is drawn on (BAC-733). Until the server says which one
     * that is -- or if it cannot -- the link falls back to the device's uid,
     * which is the right one on a hub that is its own row.
     *
     * Asked when the panel is opened, not when the sheet is drawn: most
     * sheets are looked at with the panel folded. And it is picked up again
     * from every status read, because customFetch expires an older call to
     * the same endpoint -- asking for the status while this one is in flight
     * would otherwise lose it, and with it the link. */
    const [hubUid, setHubUid] = useState(null as string | null);
    const noteHubUid = (answer: any) => {
        if (answer?.brain_uid && mounted.current) setHubUid(answer.brain_uid);
    };
    useEffect(() => {
        if (!isSuperuser || !canUseLogs || !isOpen) return;
        deviceService.minihubHubUid(uid)
            .then(found => { if (mounted.current) setHubUid(found); })
            // A link that may miss is still better than an error banner on a
            // diagnostics panel.
            .catch(() => {});
    }, [isSuperuser, canUseLogs, isOpen, uid]);

    if (!isSuperuser)
        return null;

    return (
        <section className={`${className} minihub-support mt-4`}>
            <button
                type="button"
                className="minihub-support__toggle"
                aria-expanded={isOpen}
                aria-controls={contentId}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="minihub-support__toggle-icon" aria-hidden="true">
                    <SupportIcon name="tools" />
                </span>
                <span className="minihub-support__toggle-copy">
                    <span className="minihub-support__eyebrow">
                        {t('minihub_support_badge')}
                    </span>
                    <span className="minihub-support__title">
                        {t('minihub_support_title')}
                    </span>
                </span>
                <span className={`minihub-support__chevron ${
                    isOpen ? 'minihub-support__chevron--open' : ''
                }`} aria-hidden="true" />
            </button>

            {isOpen && <div id={contentId}
                className="minihub-support__content">
                <div className="minihub-support__grid">
                    {canCheckStatus && section('status', t('minihub_support_group_status'),
                        t('minihub_support_group_status_hint'), statusSection(),
                        'minihub-support__card--featured')}
                    {section('firmware', t('minihub_support_group_firmware'),
                        t('minihub_support_group_firmware_hint'), firmwareSection())}
                    {canUseLogs && section('logs', t('minihub_support_group_logs'),
                        t('minihub_support_group_logs_hint'), logSection())}
                    {canChangeLink && section('link', t('minihub_support_group_link'),
                        t('minihub_support_group_link_hint'), linkSection())}
                    {hasAccessLists && section('cards',
                        t('minihub_support_group_lists'),
                        t('minihub_support_group_lists_hint'), listsSection(),
                        'minihub-support__card--compact')}
                    {section('pairing', t('minihub_support_group_pairing'),
                        t('minihub_support_group_pairing_hint'), pairingSection(),
                        hasAccessLists ? 'minihub-support__card--compact' : '')}
                    {section('restart', t('minihub_support_group_restart'),
                        t('minihub_support_group_restart_hint'), restartSection(),
                        'minihub-support__card--warning')}
                </div>

                {errorMessage && <div className="minihub-support__error"
                    role="alert">{errorMessage}</div>}
            </div>}
        </section>
    );

    function section(icon, title, hint, body, classNameExtra = '') {
        return (
            <article className={`minihub-support__card ${classNameExtra}`}>
                <header className="minihub-support__card-header">
                    <span className="minihub-support__card-icon" aria-hidden="true">
                        <SupportIcon name={icon} />
                    </span>
                    <span>
                        <h3>{title}</h3>
                        <p>{hint}</p>
                    </span>
                </header>
                <div className="minihub-support__card-body">{body}</div>
            </article>
        );
    }

    function field(id, label, control) {
        return (
            <div className="minihub-support__field">
                <label htmlFor={id}>{label}</label>
                {control}
            </div>
        );
    }

    function actionButton(text, onClick, options = {} as any) {
        const { disabled = false, tone = 'primary', loading = false,
            classNameExtra = '' } = options;
        return (
            <button type="button"
                className={`minihub-support__button minihub-support__button--${tone} ${classNameExtra}`}
                disabled={disabled}
                onClick={onClick}>
                {loading && <span className="minihub-support__spinner"
                    aria-hidden="true" />}
                {text}
            </button>
        );
    }

    function actionSuccess(action) {
        if (successfulAction !== action) return null;
        return (
            <div className="minihub-support__action-success" role="status">
                <SupportIcon name="check" />
                <span>{t('minihub_support_order_sent')}</span>
            </div>
        );
    }

    function statusSection() {
        return (
            <div>
                {actionButton(
                    isWaiting
                        ? t('minihub_support_status_waiting')
                        : t('minihub_support_status_ask'),
                    consultStatus,
                    { disabled: isBusy, loading: isWaiting },
                )}
                {statusError && <div className="minihub-support__status-error"
                    role="alert">{statusError}</div>}
                {status && statusRows()}
            </div>
        );
    }

    /* Known fields get a human label, while new firmware fields still show up
     * without waiting for a webapp release. */
    function statusRows() {
        return (
            <div className="minihub-support__status-result">
                <div className="minihub-support__status-success" role="status">
                    <SupportIcon name="check" />
                    <span>{t('minihub_support_status_success')}</span>
                </div>
                <dl className="minihub-support__status">
                    {Object.keys(status).map(key =>
                        <div key={key} className="minihub-support__status-item">
                            <dt>{statusLabel(key)}</dt>
                            <dd>{formatStatusValue(key, status[key])}</dd>
                        </div>
                    )}
                </dl>
            </div>
        );
    }

    function statusLabel(key) {
        const knownLabels: { [key: string]: string } = {
            version: 'minihub_support_status_version',
            uptime: 'minihub_support_status_uptime',
            link: 'minihub_support_status_link',
            internet_routing: 'minihub_support_status_link',
            received_at: 'minihub_support_status_received',
            // free_heap counts the 4 MB of PSRAM and reads healthy on a device
            // about to fall over. The internal figures are the ones that run
            // out, and the largest block is what decides whether an update
            // can even start (BAC-451).
            free_internal: 'minihub_support_status_free_internal',
            min_free_internal: 'minihub_support_status_min_free_internal',
            largest_free_internal: 'minihub_support_status_largest_free_internal',
            sim_transport: 'minihub_support_status_sim_transport',
            pairing: 'minihub_support_status_pairing',
            pairing_s: 'minihub_support_status_pairing_s',
            pairing_reason: 'minihub_support_status_pairing_reason',
            ota_last_fail: 'minihub_support_status_ota_last_fail',
            // esp_reset_reason() by name: the one that tells a panic or a
            // watchdog from an ordinary restart (the ROM codes cannot).
            reset_cause: 'minihub_support_status_reset_cause',
            reset_reason_0_name: 'minihub_support_status_reset_reason',
            reset_reason_1_name: 'minihub_support_status_reset_reason_cpu1',
            reset_reason_0: 'minihub_support_status_reset_code',
            reset_reason_1: 'minihub_support_status_reset_code_cpu1',
            log_policy: 'minihub_support_status_log_policy',
            log_auto: 'minihub_support_status_log_auto',
            log_sent_link: 'minihub_support_status_log_sent_link',
            log_sent_s: 'minihub_support_status_log_sent_s',
            signal_dbm: 'minihub_support_status_signal_dbm',
            modem_reset_hw: 'minihub_support_status_modem_reset_hw',
        };
        return knownLabels[key] ? t(knownLabels[key]) : key;
    }

    // A nested value would render as "[object Object]", which reads as a bug
    // rather than as the field it is.
    function formatStatusValue(key, value) {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        const named = STATUS_VALUE_LABELS[key]?.[String(value)];
        return named ? t(named) : String(value);
    }

    function firmwareSection() {
        return (
            <div className="minihub-support__form minihub-support__form--firmware">
                {field("minihub-version", t('minihub_support_version'),
                    <select
                        id="minihub-version"
                        value={targetVersion}
                        onChange={e => setTargetVersion(e.target.value)}
                    >
                        <option value="">{t('minihub_support_version_pick')}</option>
                        {versions.map(firmware =>
                            <option key={firmware.version} value={firmware.version}>
                                {firmware.version}
                            </option>
                        )}
                    </select>)}
                {field("minihub-half", t('minihub_support_update_half'),
                    <select
                        id="minihub-half"
                        value={targetHalf}
                        onChange={e => setTargetHalf(Number(e.target.value))}
                    >
                        <option value={DEVICE_BOTH}>{t('minihub_support_half_both')}</option>
                        <option value={DEVICE_WIFI}>{t('minihub_support_half_wifi')}</option>
                        <option value={DEVICE_BLE}>{t('minihub_support_half_ble')}</option>
                    </select>)}
                {actionButton(
                    updateProgress && !updateProgress.done && !updateProgress.failed
                        && !updateProgress.timedOut
                        ? t('minihub_support_update_following')
                        : t('minihub_support_update'),
                    updateFirmware, {
                        disabled: isBusy || isFollowingUpdate || !targetVersion,
                        loading: isFollowingUpdate,
                        classNameExtra: 'minihub-support__form-action',
                    })}
                {updateProgress ? updateProgressRows() : actionSuccess('firmware')}
                {isFollowingUpdate && <UpdateWait startedAt={updateStartedAt} hasSim={hasSim} />}
            </div>
        );
    }

    function updateProgressRows() {
        const { phases = [], done, failed, timedOut } = updateProgress;
        const verdict = done ? 'minihub_support_update_done'
            : failed ? 'minihub_support_update_failed'
            : timedOut ? 'minihub_support_update_timeout' : '';
        const tone = done ? 'success' : (failed || timedOut) ? 'error' : 'pending';
        return (
            <div className={`minihub-support__update-progress minihub-support__update-progress--${tone}`}
                data-testid="minihub-update-progress"
                role={failed || timedOut ? 'alert' : 'status'}>
                {phases
                    // The settled OK is the verdict's job to say; a
                    // failure stays, it is the thing to read.
                    .filter(({ status }) => !(done && UPDATE_SETTLED.includes(status)))
                    .map(({ status, percent }, i, shown) => {
                        // A phase that was followed by another one finished,
                        // whatever percentage the last poll happened to see:
                        // "Downloading 82%" under "Installing" reads as a
                        // download that stopped short.
                        const current = i === shown.length - 1 && !done;
                        const label = UPDATE_STATUS_LABELS[status]
                            ? t(UPDATE_STATUS_LABELS[status]) : String(status);
                        const withPercent = current && percent !== null
                            && percent !== undefined
                            && UPDATE_IN_PROGRESS.includes(status);
                        return (
                            <span key={i} className={`minihub-support__update-phase ${
                                current ? '' : 'minihub-support__update-phase--past'}`}>
                                {current ? '' : '\u2713 '}{label}
                                {withPercent ? ` ${percent}%` : ''}
                            </span>
                        );
                    })}
                {verdict && <span className="minihub-support__update-verdict">
                    {t(verdict)}
                </span>}
            </div>
        );
    }

    function logSection() {
        return (
            <div className="minihub-support__form">
                {field("minihub-level", t('minihub_support_log_level'),
                    <select
                        id="minihub-level"
                        value={level}
                        onChange={e => setLevel(e.target.value)}
                    >
                        {LOG_LEVELS.map(name =>
                            <option key={name} value={name}>{name}</option>
                        )}
                    </select>)}
                <div className="minihub-support__check-field">
                    <input
                        id="minihub-persist"
                        type="checkbox"
                        checked={persist}
                        onChange={e => setPersist(e.target.checked)}
                    />
                    <label htmlFor="minihub-persist">
                        {t('minihub_support_persist')}
                    </label>
                </div>
                {field("minihub-at-pct", t('minihub_support_log_at_pct'),
                    <input
                        id="minihub-at-pct"
                        type="number"
                        min={0}
                        max={100}
                        value={atPct}
                        onChange={e => setAtPct(e.target.value)}
                    />)}
                {field("minihub-min-gap", t('minihub_support_log_min_gap'),
                    <input
                        id="minihub-min-gap"
                        type="number"
                        min={0}
                        max={1440}
                        value={minGapMin}
                        onChange={e => setMinGapMin(e.target.value)}
                    />)}
                <div className="minihub-support__button-row minihub-support__form-action">
                    {actionButton(t('minihub_support_apply_level'),
                        () => run(devicesActions.minihubLogConfig(uid, {
                            level,
                            save: persist,
                            at_pct: clampInt(atPct, 0, 100),
                            // Typed in minutes, seconds on the wire.
                            min_gap: clampInt(minGapMin, 0, 1440) * 60,
                        })), { disabled: isBusy })}
                    {actionButton(t('minihub_support_fetch_logs'),
                        () => run(devicesActions.minihubLogFetch(uid)), {
                            disabled: isBusy,
                            tone: 'secondary',
                        })}
                </div>
                <a className="minihub-support__grafana-link"
                    href={grafanaDeviceLogsUrl(hubUid ?? uid)}
                    target="_blank"
                    rel="noopener noreferrer">
                    {t('minihub_support_grafana_logs')}
                </a>
            </div>
        );
    }

    function linkSection() {
        return (
            <div className="minihub-support__form minihub-support__form--inline">
                {field("minihub-link", t('minihub_support_link_mode'),
                    <select
                        id="minihub-link"
                        value={linkMode}
                        onChange={e => setLinkMode(e.target.value)}
                    >
                        {LINK_MODES.map(name =>
                            <option key={name} value={name}>{name}</option>
                        )}
                    </select>)}
                {actionButton(t('minihub_support_apply_link'),
                    () => run(devicesActions.minihubLink(uid, linkMode)), {
                        disabled: isBusy,
                        classNameExtra: 'minihub-support__form-action',
                    })}
            </div>
        );
    }

    function listsSection() {
        return (
            <div className="minihub-support__button-stack">
                {hasNfc && actionButton(t('minihub_support_resync_nfc'),
                    () => run(devicesActions.minihubResync(uid, 'nfc'),
                        'lists'), {
                        disabled: isBusy,
                        tone: 'secondary',
                    })}
                {hasKeypad && actionButton(t('minihub_support_resync_keypad'),
                    () => run(devicesActions.minihubResync(uid, 'keypad'),
                        'lists'), {
                        disabled: isBusy,
                        tone: 'secondary',
                    })}
                {actionSuccess('lists')}
            </div>
        );
    }

    /* "Close" only where both halves honour it (supportsSupportOrder): before
     * 2026.08.022 the ESP32 ignored the field that says which way, so "close"
     * REOPENED the 120 s BLE window on a customer's device -- the exact
     * opposite of what the button says -- and the EFR32 only learnt to leave
     * pairing in 4.6.7. */
    function pairingSection() {
        return (
            <div className="minihub-support__button-stack">
                {actionButton(t('minihub_support_pairing_open'),
                    () => run(devicesActions.minihubPairing(uid, true),
                        'pairing'), {
                        disabled: isBusy,
                        tone: 'secondary',
                    })}
                {canClosePairing && actionButton(t('minihub_support_pairing_close'),
                    () => run(devicesActions.minihubPairing(uid, false),
                        'pairing'), {
                        disabled: isBusy,
                        tone: 'secondary',
                    })}
                {actionSuccess('pairing')}
            </div>
        );
    }

    function restartSection() {
        return (
            <div className="minihub-support__button-grid">
                {actionButton(t('minihub_support_restart_wifi'),
                    () => run(devicesActions.minihubRestart(uid, 'wifi'),
                        'restart'), {
                        disabled: isBusy,
                        tone: 'warning',
                    })}
                {actionButton(t('minihub_support_restart_ble'),
                    () => run(devicesActions.minihubRestart(uid, 'ble'),
                        'restart'), {
                        disabled: isBusy,
                        tone: 'warning',
                    })}
                {actionButton(t('minihub_support_restart_both'),
                    () => run(devicesActions.minihubRestart(uid, 'both'),
                        'restart'), {
                        disabled: isBusy,
                        tone: 'warning',
                        classNameExtra: 'minihub-support__button--wide',
                    })}
                {actionSuccess('restart')}
            </div>
        );
    }

    /*
     * Un solo botón: se pide el estado y se espera a que llegue.
     *
     * Que la respuesta viaje por MQTT y haya que sondear al servidor es un
     * detalle de por dónde va el dato, no algo que deba hacer quien mira la
     * pantalla: la primera versión tenía dos botones ("pedir" y "leer") y era
     * incomprensible.
     *
     * Antes de enviar la orden se lee el último `received_at`. Así, incluso en
     * la primera consulta de esta sesión, un estado guardado hace una hora no
     * se presenta como si acabara de llegar.
     */
    async function consultStatus() {
        const previoPintado = status?.received_at ?? null;
        setIsBusy(true);
        setIsWaiting(true);
        setStatus(null);
        setStatusError('');
        setSuccessfulAction('');
        setError('');
        try {
            let previo = previoPintado;
            if (previo === null) {
                const anterior: any = await dispatch(
                    devicesActions.minihubStatusRead(uid));
                noteHubUid(anterior);
                previo = anterior?.status?.received_at ?? null;
            }
            await dispatch(devicesActions.minihubStatusAsk(uid));

            const limite = Date.now() + statusWaitMs;
            while (Date.now() < limite) {
                await new Promise(r => setTimeout(r, statusPollMs));
                const respuesta: any = await dispatch(
                    devicesActions.minihubStatusRead(uid));
                noteHubUid(respuesta);
                const leido = respuesta?.status;
                if (leido && typeof leido === 'object'
                        && leido.received_at != null
                        && leido.received_at !== previo) {
                    setStatus(leido);
                    return;
                }
            }
            setStatusError(t('minihub_support_status_timeout'));
        } catch (e) {
            setStatusError(statusErrorText(e));
        } finally {
            setIsBusy(false);
            setIsWaiting(false);
        }
    }

    function updateFirmware() {
        // Downgrades are allowed on purpose -- getting a box off a bad release
        // is the whole point -- but they are asked about, because an old
        // enough image may not be able to update itself back.
        if (isDowngrade()) {
            confirmWith(t('minihub_support_update'),
                t('minihub_support_downgrade_confirm'), sendUpdate);
            return;
        }
        sendUpdate();
    }

    async function sendUpdate() {
        setIsBusy(true);
        setSuccessfulAction('');
        setError('');
        setUpdateProgress(null);
        let before: any = null;
        try {
            // What django has BEFORE the order: it writes only update_version
            // when sending it and leaves update_status as it was, so the
            // failure of the previous update is still there when this one
            // starts. Seeing that same word again is "not started yet".
            try {
                before = await deviceService.getDeviceInfo(uid);
            } catch {
                before = null;
            }
            await deviceService.updateDeviceFirmwareVersion(
                uid, targetVersion, targetHalf);
            setSuccessfulAction('firmware');
        } catch (e) {
            setError(errorText(e));
            return;
        } finally {
            setIsBusy(false);
        }
        // Not under isBusy: the status and the restarts stay usable while
        // this follows, only the update button itself is held.
        await followUpdate(before);
    }

    /* The firmware reports each phase (downloading with a percentage,
     * installing BLE, resetting, ok) and django keeps the last one on the
     * device, so the panel reads the device until it is either back on the
     * version that was ordered or has said why not. The order is what django
     * had before the device answered, so an OK with the old version is "not
     * started yet", not "done". For the BLE half alone the global version does
     * not move; there, an OK after any in-progress phase is the end. */
    async function followUpdate(before) {
        setUpdateStartedAt(Date.now());
        setIsFollowingUpdate(true);
        let sawProgress = false;
        const sameAsBefore = (device) => before && typeof before === 'object'
            && device.update_status === before.update_status
            && device.update_percent === before.update_percent
            && device.version === before.version;
        let phases = [] as any[];
        const limit = Date.now() + updateWaitMs;
        // A new phase is appended; the same phase again only moves its
        // percentage, so a download shows as one line that fills up.
        const report = (status, percent, verdict = {}) => {
            const last = phases[phases.length - 1];
            phases = last && last.status === status
                ? [...phases.slice(0, -1), { status, percent }]
                : [...phases, { status, percent }];
            setUpdateProgress({ phases, ...verdict });
        };
        try {
            while (mounted.current && Date.now() < limit) {
                await new Promise(r => setTimeout(r, statusPollMs));
                // Checked after the sleep as well: a panel closed while this
                // slept must not fire one more read on its way out.
                if (!mounted.current) return;
                // The service, not devicesActions.getDevice: that one names
                // the location from state.locations, which the device sheet
                // does not promise to have, and swallows the failure.
                let device: any;
                try {
                    device = await deviceService.getDeviceInfo(uid);
                } catch {
                    continue;
                }
                if (!mounted.current) return;
                if (!device || typeof device !== 'object') continue;
                const status = device.update_status;
                const percent = device.update_percent;
                if (UPDATE_IN_PROGRESS.includes(status)) {
                    sawProgress = true;
                    // A download after the EFR32 is the ESP32 fetching its
                    // own image (BAC-757): it sends no percentage, and over
                    // the SIM it can take 25 min.
                    const afterBle = status === DEVICE_UPDATE_STATUS.DOWNLOADING
                        && phases.some(p => p.status === DEVICE_UPDATE_STATUS.INSTALLING_BLE);
                    if (afterBle) report(INSTALLING_WIFI, null);
                    else report(status, percent);
                    continue;
                }
                if (UPDATE_SETTLED.includes(status)) {
                    const arrived = targetHalf === DEVICE_BLE
                        ? sawProgress
                        : device.version === targetVersion;
                    if (arrived) {
                        report(status, percent, { done: true });
                        return;
                    }
                    // Settled but not on the new version yet, with the
                    // download (and the EFR32) behind: the ESP32 is on its
                    // own half now, which it does not report.
                    if (sawProgress) report(INSTALLING_WIFI, null);
                    continue;
                }
                if (!sawProgress && sameAsBefore(device)) continue;
                report(status, percent, { failed: true });
                return;
            }
            if (mounted.current)
                setUpdateProgress({ phases, timedOut: true });
        } finally {
            if (mounted.current) setIsFollowingUpdate(false);
        }
    }

    function isDowngrade() {
        // Always against `version`, whichever half is being updated. The list
        // in the dropdown is in the combined YEAR.MONTH.SEQ numbering, and so
        // is `version`; `versionBle` is the EFR32's own (4.6.7), so comparing
        // the two answered "2026 > 4, it is an upgrade" every single time and
        // the warning never appeared for the BLE side.
        return compareVersions(targetVersion, version) < 0;
    }

    function confirmWith(title, body, onAccept) {
        dispatch(popUpActions.showPopUp(GLOBAL_POPUPS.BASE_APP_GLOBAL, {
            title,
            body,
            onAccept,
            acceptButtonName: t('global_accept'),
            cancelButtonName: t('global_cancel'),
            limitWidth: true,
        }));
    }

    /**
     * Negative if a is older than b, 0 if they are the same or not comparable.
     *
     * Field by field and numerically, not as strings: without zero padding
     * "2026.08.9" sorts after "2026.08.10", so a plain `<` called an upgrade a
     * downgrade and stayed quiet on a real one.
     *
     * A different NUMBER of fields, or anything that is not a number, returns
     * 0 and the confirmation simply does not claim anything. Two versions with
     * the same shape are compared even if they belong to different schemes
     * (4.5.14 against 2026.08.001), and that is fine here: both callers pass
     * versions from the same list.
     */
    function compareVersions(a, b) {
        if (!a || !b) return 0;
        const left = String(a).split('.').map(Number);
        const right = String(b).split('.').map(Number);
        if (left.length !== right.length) return 0;
        if (left.some(isNaN) || right.some(isNaN)) return 0;
        for (let i = 0; i < left.length; i++) {
            if (left[i] !== right[i]) return left[i] < right[i] ? -1 : 1;
        }
        return 0;
    }

    /* With rethrow on, genericAsyncAction no longer raises the global alert,
     * so this panel is the only place the failure shows. A support tool that
     * answers every failure with the same sentence hides the one thing worth
     * reading -- "device disconnected", a 403 -- so the server's message wins
     * when there is one. */
    function errorText(e) {
        const message = e?.message || e?.toString?.();
        return message ? `${t('minihub_support_error')}: ${message}`
            : t('minihub_support_error');
    }

    function statusErrorText(e) {
        const message = e?.message || e?.toString?.();
        if (message && /active brain.*(no existe|does not exist)/i.test(message))
            return t('minihub_support_status_brain_missing');
        return message ? `${t('minihub_support_status_error')}: ${message}`
            : t('minihub_support_status_error');
    }

    async function run(action, successAction = '') {
        setIsBusy(true);
        setSuccessfulAction('');
        setError('');
        try {
            await dispatch(action);
            if (successAction) setSuccessfulAction(successAction);
        } catch (e) {
            setError(errorText(e));
        } finally {
            setIsBusy(false);
        }
    }
}

/* How long the update has been followed, and -- on a box that may be on its
 * SIM -- how long it can take: the ESP32's stretch reports nothing for many
 * minutes, and a clock that moves is what keeps that from reading as hung.
 * Its own component so the tick re-renders this line, not the whole panel. */
function UpdateWait({ startedAt, hasSim }) {
    const { t } = useTranslation();
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
    const elapsed = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    return (
        <div className="minihub-support__update-wait" data-testid="minihub-update-wait">
            <span>{t('minihub_support_update_elapsed', { elapsed })}</span>
            {hasSim && <span>{t('minihub_support_update_sim_hint')}</span>}
        </div>
    );
}

function SupportIcon({ name }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            focusable="false" aria-hidden="true">
            {path()}
        </svg>
    );

    function path() {
        switch (name) {
            case 'tools':
                return <path d="M14.7 6.3a4 4 0 0 0-5-5L12 3.6 9.6 6 7.3 3.7a4 4 0 0 0 5 5l-7.8 7.8a2.1 2.1 0 1 0 3 3l7.8-7.8a4 4 0 0 0 5-5L18 9l-2.4-2.4 2.3-2.3a4 4 0 0 0-3.2 2z" />;
            case 'status':
                return <>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M7 12h2l1.5-3 3 6 1.5-3h2" />
                </>;
            case 'firmware':
                return <>
                    <path d="M12 3v11m0 0 4-4m-4 4-4-4" />
                    <path d="M5 18v2h14v-2" />
                </>;
            case 'logs':
                return <>
                    <path d="M6 3h9l3 3v15H6z" />
                    <path d="M15 3v4h4M9 11h6M9 15h6" />
                </>;
            case 'link':
                return <>
                    <path d="M4.5 9a11 11 0 0 1 15 0M7.5 12a7 7 0 0 1 9 0M10.5 15a3 3 0 0 1 3 0" />
                    <circle cx="12" cy="18" r=".7" fill="currentColor" stroke="none" />
                </>;
            case 'cards':
                return <>
                    <rect x="3" y="6" width="14" height="11" rx="2" />
                    <path d="M7 3h12a2 2 0 0 1 2 2v9M6 10h4M6 13h7" />
                </>;
            case 'pairing':
                return <path d="m12 3 5 5-5 4 5 4-5 5V3Zm0 9L7.5 8M12 12l-4.5 4" />;
            case 'restart':
                return <>
                    <path d="M20 7v5h-5" />
                    <path d="M18.5 16a8 8 0 1 1 .8-7.5L20 12" />
                </>;
            case 'check':
                return <path d="m6.5 12.5 3.5 3.5 7.5-8" />;
            case 'info':
            default:
                return <>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 11v5M12 8h.01" />
                </>;
        }
    }
}
